import { getReferrer } from '@/shared/features/enter-referrer-tracking';
import { type EventProperties } from '@/utils/analytics';
import type { BuildInfoProviderType } from '@/views/build/context/BuildInfoProvider';
import { getBuildType } from '@/views/build/util/getBuildType';

/**
 * Build tracking event properties 생성 (동기 함수)
 *
 * SafeEnterTracking에서 client-side에서만 호출됨.
 * Hook이 아니므로 useEffect 없이 동기로 실행되어 race condition을 방지합니다.
 *
 * @param buildInfo - BuildInfoContext 값
 * @returns EventProperties - logger.track에 전달할 프로퍼티
 *
 * @example
 * ```typescript
 * const buildInfo = useBuildInfoContext();
 * const properties = getBuildTrackingProperties(buildInfo);
 * // properties = {
 * //   build_type: 'create',
 * //   from: '/',
 * //   location: 'home_cta',
 * //   origin_quiz_set_id: 'quiz-123' // optional
 * // }
 * ```
 */
export function getBuildTrackingProperties(
  buildInfo: BuildInfoProviderType,
): EventProperties {
  // 1. Build type 추출 (CREATE/UPDATE/DUPLICATE -> lowercase)
  const buildType = getBuildType(buildInfo.action);

  // 2. Referrer 동기 추출 (localStorage 직접 접근)
  const referrer = getReferrer();

  // 3. Origin quiz set ID (복사/업데이트 시에만 존재)
  const originQuizSetId = buildInfo.originQuizSet?.id;

  // 4. Properties 조합
  const properties: EventProperties = {
    build_type: buildType,
  };

  // 선택적 프로퍼티들
  if (originQuizSetId) {
    properties.origin_quiz_set_id = originQuizSetId;
  }

  if (referrer?.from) {
    properties.from = referrer.from;
  }
  if (referrer?.location) {
    properties.location = referrer.location;
  }

  return properties;
}
